import { Dispatch, SetStateAction } from "react";
import { customStyle, defaultStyle } from "../../../../../constant/custom-style";
import Input from "../../../../ui/input";

interface Props {
    availableMonths: string[];
    bulkValue: string;
    setBulkValue: (value: string) => void;
    selectedMonths: string[];
    setSelectedMonths: (value: string[]) => void;
    bulkWarning: string | null;
    setBulkWarning: (value: string | null) => void;
    isBulkFillOpen: boolean;
    setIsBulkFillOpen: (value: boolean) => void;
    handleSaveBulk: () => void;
    typeOfAgeBracket: string;
}

export default function BulkFillMonthsModal({
    setSelectedMonths,
    selectedMonths,
    availableMonths,
    bulkValue,
    setBulkValue,
    bulkWarning,
    setBulkWarning,
    isBulkFillOpen,
    setIsBulkFillOpen,
    handleSaveBulk,
    typeOfAgeBracket
}: Props) {
    if (!isBulkFillOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3">
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="bulk-fill-title"
                className="flex max-h-[85dvh] w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-lg"
            >
                {/* HEADER */}
                <div className="flex items-center justify-between border-b border-gray-200 bg-[#f8fafc] px-3.5 py-2.5">
                    <div className="min-w-0">
                        <h2 id="bulk-fill-title" className="text-sm font-semibold text-gray-800">
                            Bulk Fill Months
                        </h2>
                        <p className="text-[11px] text-gray-500">
                            {`Apply one value to selected months across ages ${typeOfAgeBracket}.`}
                        </p>
                    </div>
{/* 
                    <button
                        type="button"
                        onClick={() => setIsBulkFillOpen(false)}
                        aria-label="Close modal"
                        className="ml-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                    >
                        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="m18 6-12 12" />
                            <path d="m6 6 12 12" />
                        </svg>
                    </button> */}
                </div>

                {/* CONTENT */}
                <div className="min-h-0 space-y-3 overflow-y-auto px-3.5 py-3">
                    <div>
                        <div className="mb-1.5 flex items-center justify-between gap-2">
                            <label className="text-xs font-semibold text-gray-700">
                                Select months
                            </label>
                            <button
                                type="button"
                                onClick={() => setSelectedMonths(selectedMonths.length === availableMonths.length ? [] : [...availableMonths]) }
                                className="text-[11px] font-medium text-[#186cb2] hover:underline"
                            >
                                {selectedMonths.length === availableMonths.length ? "Clear all" : "Select all"}
                            </button>
                        </div>
                        <div className="grid max-h-48 grid-cols-2 gap-1.5 overflow-y-auto rounded-md border border-gray-200 p-2 sm:grid-cols-3">
                            {availableMonths.map((month) => {
                                const isSelected = selectedMonths.includes(month);
                                return (
                                    <label
                                        key={month}
                                        className={`flex min-w-0 cursor-pointer items-start gap-2 rounded border p-2 transition-colors duration-200 ${
                                            isSelected ? "border-[#186cb2] bg-[#186cb2]/10 shadow-sm" : "border-gray-200 bg-white hover:bg-gray-50" }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={(event) =>
                                                setSelectedMonths(
                                                    event.target.checked
                                                        ? selectedMonths.includes(month)
                                                            ? selectedMonths
                                                            : [...selectedMonths, month]
                                                        : selectedMonths.filter((item) => item !== month)
                                                )
                                            }
                                            className="sr-only"
                                        />
                                        <div
                                            aria-hidden="true"
                                            className={`mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border transition-colors ${
                                                isSelected ? "border-[#186cb2] bg-[#186cb2] text-white" : "border-gray-400 bg-white text-transparent" }`}
                                        >
                                            {isSelected && (
                                                <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="m5 12 4 4L19 6" />
                                                </svg>
                                            )}
                                        </div>
                                        <span className={`min-w-0 break-words text-xs leading-4 ${ isSelected ? "font-medium text-[#186cb2]" : "text-gray-700" }`} >
                                            {month}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                        <p className="mt-1 text-[10px] text-gray-500">
                            {selectedMonths.length} of {availableMonths.length} months selected
                        </p>
                    </div>
                    <div>
                        <label
                            htmlFor="bulk-fill-value"
                            className="mb-1 block text-xs font-semibold text-gray-700"
                        >
                            Value to apply
                        </label>
                        <Input
                            type="text"
                            name="groupName"
                            value={bulkValue}
                            isCustomStyle={true}
                            customCss={`${defaultStyle} ${!bulkValue ? "" : customStyle}`}
                            onChange={(event) => {
                                setBulkValue(event.target.value);
                                setBulkWarning(null);
                            }}
                            placeholder="N/A, FREE, or a number"
                        />
                        <p className="mt-1 text-[10px] leading-4 text-gray-500">
                            Enter N/A, FREE, or a number (e.g. 0, 2, .222, 1.1, 1222.2).
                        </p>
                    </div>
                    {bulkWarning && (
                        <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-2.5 py-2 text-[11px] text-red-600" >
                            {bulkWarning}
                        </div>
                    )}
                    <p className="text-[10px] leading-4 text-gray-500">
                        {`Existing values for the selected months at ages ${typeOfAgeBracket} will be replaced.`}
                    </p>
                </div>
                {/* FOOTER */}
                <div className="flex justify-end gap-1.5 border-t border-gray-200 bg-gray-50 px-3.5 py-2">
                    <button
                        type="button"
                        onClick={() => {
                            setIsBulkFillOpen(false)
                            setSelectedMonths([])
                            setBulkValue('N/A')
                            setBulkWarning(null)
                        }}
                        className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-[11px] font-medium text-gray-700 transition-colors hover:bg-gray-100"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSaveBulk}
                        disabled={selectedMonths.length === 0}
                        className="rounded-md bg-[#186cb2] px-3 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-[#145a95] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Save {selectedMonths.length > 0 ? `(${selectedMonths.length})` : ""}
                    </button>
                </div>
            </div>
        </div>
    );
}